import { useState } from 'react';
import {
  ApiError,
  cancelPoolShipping,
  type ApiClient,
  type IframePoolShippingDetail,
} from '../../../modules/api';

export interface CancelDeliveryCallbacks {
  /** Cancel succeeded — receives the updated delivery (status `canceled`). */
  onCanceled: (shipping: IframePoolShippingDetail) => void;
  /** 422 `SHIPPING_NOT_CANCELABLE` — the cancel window closed while the
   *  confirmation was open (fulfilment already started). */
  onNotCancelable: () => void;
}

/**
 * The cancel request behind a delivery's confirmation step: on confirm the
 * cards are bought back at their regular buyback price and the shipping +
 * handling charges are refunded to the wallet balance. Any error except
 * `SHIPPING_NOT_CANCELABLE` stays on the step so the player can retry (the
 * backend call is retriable).
 */
export function useCancelDelivery(
  client: ApiClient,
  shippingId: number,
  { onCanceled, onNotCancelable }: CancelDeliveryCallbacks,
) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      onCanceled(await cancelPoolShipping(client, shippingId));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SHIPPING_NOT_CANCELABLE') {
        onNotCancelable();
        return;
      }
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not cancel the delivery. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, error, confirm };
}
